import { useQueryClient } from "@tanstack/react-query";

import { FileUploadStatus } from "components/bid_module/types";
import { graphql } from "lib/generated/app-service-gql";
import {
  CustomerDocumentProcessingStatusSubscription,
  CustomerDocumentProcessingStatusSubscriptionVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "lib/tracking";
import { CustomerDocumentType } from "lib/types/graphQLEnums";
import { useSubscription } from "../useGraphQlClient";
import { customerDocumentsKey } from "./useCustomerDocuments";

// adding this so it generates the types
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _customerDocumentProcessingStatusSubscription = graphql(`
  subscription customerDocumentProcessingStatus($request: CustomerDocumentProcessingRequest!) {
    customerDocumentProcessingStatus(CustomerDocumentProcessingRequest: $request) {
      status
      customerDocumentId
      output {
        fileName
        uiPath
      }
    }
  }
`);

type Props = {
  resourceId?: string;
  documentType: CustomerDocumentType;
  updateFileStatus: ({
    fileName,
    documentId,
    updates,
  }: {
    fileName: string | null | undefined;
    documentId: string | null | undefined;
    updates: Partial<FileUploadStatus>;
  }) => void;
  enabled: boolean;
};

/**
 * Contains the logic for handling the processing status of a
 * customer document.
 * @param param0
 * @returns
 */
export function useCustomerDocumentProcessingStatus({
  resourceId,
  documentType,
  updateFileStatus,
  enabled,
}: Props) {
  const client = useQueryClient();

  const { logEvent } = useTracking();

  const { data, ...rest } = useSubscription<
    CustomerDocumentProcessingStatusSubscription,
    CustomerDocumentProcessingStatusSubscriptionVariables
  >(
    `
    subscription customerDocumentProcessingStatus($request: CustomerDocumentProcessingRequest!) {
    customerDocumentProcessingStatus(CustomerDocumentProcessingRequest: $request) {
        status
        customerDocumentId
        output {
          fileName
          uiPath
        }
      }
    }
`,
    {
      request: {
        resourceId,
        documentType,
      },
    },
    {
      enabled: !!documentType && enabled,
      onData: async (data) => {
        const { status, customerDocumentId, output } = data.customerDocumentProcessingStatus;

        const { fileName, uiPath } = output || {};
        if (status === "success") {
          logEvent(EventNames.customerDocumentProcessedSuccessfully, {
            "Resource ID": resourceId,
            "Document Type": documentType,
          });

          // Update query cache to reflect the successful processing of the document
          await client.invalidateQueries({
            queryKey: customerDocumentsKey({ documentType, resourceId, uiPath }),
            exact: false, // Allow matching without pagination;
          });

          // Update documentId and isProcessed status for the file
          updateFileStatus({
            fileName,
            documentId: customerDocumentId,
            updates: {
              isProcessed: true,
              isFinished: true,
              documentId: customerDocumentId,
              isError: false,
            },
          });
        } else {
          logEvent(EventNames.customerDocumentProcessingError, {
            "Resource ID": resourceId,
            "Document Type": documentType,
          });
          updateFileStatus({
            fileName,
            documentId: customerDocumentId,
            updates: {
              isError: true,
              isFinished: true,
              isProcessed: false,
              documentId: customerDocumentId,
            },
          });
        }
      },
    },
  );
  return { data, updateFileStatus, ...rest };
}
